import React, { useMemo, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { V3GameState } from '../../types/V3_Core';
import { sessionManager } from '../../utils/sessionManager';
import VerdictModal from './VerdictModal';
import HelpModal from '../ui/HelpModal';

interface GameArenaUIProps {
    v3State: V3GameState;
    localPlayerId: string;
    onProbe: (targetId: string, cellIndex: number) => void;
    onVerdict: (isCorrect: boolean) => void;
    isTutorialMode?: boolean;
    tutorialHighlight?: {
        targetPlayerId?: string;
        cellIndices?: number[];
        element?: 'HUD' | 'GRID' | 'VERDICT';
    };
    revealAll?: boolean;
}

/** Génère une teinte HSL stable depuis un string (pseudo) */
const stringToHue = (str: string): number => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    return Math.abs(hash) % 360;
};

/** Détecte le type d'un message de log pour colorisation */
const getLogClass = (entry: string): string => {
    const lower = entry.toLowerCase();
    if (lower.includes('hit') || lower.includes('souvenir') || lower.includes('révélé') || lower.includes('touche')) return 'log-hit';
    if (lower.includes('miss') || lower.includes('vide') || lower.includes('manqué')) return 'log-miss';
    if (lower.includes('verdict') || lower.includes('correct') || lower.includes('faux')) return 'log-verdict';
    return 'log-event';
};

const getLogIcon = (entry: string): string => {
    const lower = entry.toLowerCase();
    if (lower.includes('hit') || lower.includes('souvenir') || lower.includes('révélé') || lower.includes('touche')) return '🎯';
    if (lower.includes('miss') || lower.includes('vide') || lower.includes('manqué')) return '💨';
    if (lower.includes('verdict') || lower.includes('correct') || lower.includes('faux')) return '⚖️';
    return '🔍';
};

const getPhaseName = (phase: string): string => {
    switch (phase) {
        case 'PROBE': return 'Exploration';
        case 'VERDICT': return 'Révélation';
        case 'SETUP': return 'Préparation';
        case 'PLAYING': return 'Résonance';
        case 'FINISHED': return 'Clôture';
        default: return phase;
    }
};

// ─────────────────────────────────────────────────────
// COMPOSANT : Cellule d'une grille adversaire
// ─────────────────────────────────────────────────────
interface OpponentCellProps {
    cell: string;
    index: number;
    isTargetable: boolean;
    onProbe: () => void;
    revealAll: boolean;
}

const OpponentCell: React.FC<OpponentCellProps> = ({ cell, index, isTargetable, onProbe, revealAll }) => {
    const visualCell = (!revealAll && (cell === 'SOUVENIR' || cell === 'SHADOW_ZONE')) ? 'EMPTY' : cell;

    let cellClass = 'relative overflow-visible ';
    if (visualCell === 'EMPTY') cellClass += 'bg-slate-900/40 ';
    if (visualCell === 'REVEALED_EMPTY') cellClass += 'bg-black cell-miss-fx border border-slate-800/30 ';
    if (visualCell === 'REVEALED_HIT') cellClass += 'bg-amber-500 cell-hit-fx z-10 ';
    if (isTargetable && visualCell === 'EMPTY') cellClass += 'grid-cell-targetable cursor-crosshair ';

    return (
        <motion.div
            key={index}
            whileHover={isTargetable && visualCell === 'EMPTY' ? { backgroundColor: 'rgba(34,211,238,0.18)', scale: 1.05 } : {}}
            whileTap={isTargetable ? { scale: 0.9 } : {}}
            onClick={(e) => { e.stopPropagation(); if (isTargetable && visualCell === 'EMPTY') onProbe(); }}
            className={cellClass}
        >
            {visualCell === 'REVEALED_HIT' && (
                <div className="absolute inset-0 flex items-center justify-center text-[8px] z-20 select-none">💥</div>
            )}
            {/* Micro dot on empty cells */}
            {visualCell === 'EMPTY' && (
                <div className="absolute inset-0 flex items-center justify-center opacity-20">
                    <div className="w-[2px] h-[2px] rounded-full bg-slate-500" />
                </div>
            )}
        </motion.div>
    );
};

// ─────────────────────────────────────────────────────
// COMPOSANT : Cellule de mon plateau
// ─────────────────────────────────────────────────────
const MyCell: React.FC<{ cell: string }> = ({ cell }) => {
    let cls = 'rounded-[1px] relative z-10 ';
    if (cell === 'SOUVENIR') cls += 'bg-purple-900/60 border border-purple-500/40 souvenir-pulse ';
    else if (cell === 'SHADOW_ZONE') cls += 'bg-red-900/50 border border-red-600/40 shadow-zone-fx ';
    else if (cell === 'REVEALED_HIT') cls += 'bg-amber-500 shadow-[0_0_15px_#fbbf24] z-20 ';
    else if (cell === 'REVEALED_EMPTY') cls += 'bg-black border border-slate-800 ';
    else cls += 'bg-slate-900/40 ';

    return (
        <div className={cls}>
            {cell === 'SOUVENIR' && (
                <div className="absolute inset-0 flex items-center justify-center opacity-40 text-[7px] select-none">✦</div>
            )}
            {cell === 'SHADOW_ZONE' && (
                <div className="absolute inset-0 flex items-center justify-center opacity-30 text-[6px] select-none">⚠</div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL
// ─────────────────────────────────────────────────────
const GameArenaUI: React.FC<GameArenaUIProps> = ({
    v3State,
    localPlayerId,
    onProbe,
    onVerdict,
    isTutorialMode = false,
    tutorialHighlight,
    revealAll = false
}) => {
    const [showHelp, setShowHelp] = useState(false);

    const { opponents, localPlayer, activePlayerId } = useMemo(() => {
        if (!v3State?.players) return { opponents: [], localPlayer: null, activePlayerId: null };
        const local = v3State.players.find(p => p.id === localPlayerId) || null;
        const others = v3State.players.filter(p => p.id !== localPlayerId);
        return { opponents: others, localPlayer: local, activePlayerId: v3State.activePlayerId };
    }, [v3State, localPlayerId]);

    const isMyTurn = activePlayerId === localPlayerId;

    if (!v3State) return (
        <div className="flex items-center justify-center h-screen bg-[#020617] text-cyan-500 font-mono animate-pulse">
            INITIALISATION DU PLATEAU...
        </div>
    );

    return (
        <div className="h-full w-full bg-slate-950 overflow-hidden flex flex-col font-sans relative text-slate-200 transition-colors duration-500 scanlines">

            {/* ── NÉBULEUSE BACKGROUND (Softened) ── */}
            <div className="nebula-bg" aria-hidden>
                <div className="nebula-orb nebula-orb-1" />
                <div className="nebula-orb nebula-orb-2" />
                <div className="nebula-orb nebula-orb-3" />
            </div>

            {/* ── VIGNETTE ── */}
            <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,transparent_30%,rgba(0,0,0,0.75)_100%)] z-20" />

            {/* ── PACK HEADER ── */}
            {v3State.availableMemories && v3State.availableMemories.length > 0 && (
                <motion.div
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    className="fixed top-4 left-1/2 -translate-x-1/2 z-40"
                >
                    <div className="px-6 py-1.5 bg-white/5 border border-white/10 rounded-full backdrop-blur-md shadow-lg">
                        <div className="text-slate-200 text-xs font-serif italic tracking-wide">
                            Collection : [{v3State.availableMemories.length} souvenirs restants]
                        </div>
                    </div>
                </motion.div>
            )}

            {/* ── LAYOUT PRINCIPAL ── */}
            <div className="flex-1 w-full max-w-[95vw] 2xl:max-w-[1800px] mx-auto flex flex-col justify-between overflow-hidden relative z-10 h-full py-2">

                {/* ════════════════════════════════════════
                    ADVERSAIRES (zone haute)
                    ════════════════════════════════════════ */}
                <div className={`
                    w-full flex-none lg:flex-1
                    flex lg:flex-row flex-row items-center justify-center gap-4 lg:gap-16
                    relative p-4 pt-10
                    transition-all duration-500 overflow-x-auto lg:overflow-visible
                    scrollbar-hide snap-x snap-mandatory
                    ${isMyTurn ? 'scale-95 opacity-50 grayscale-[0.3]' : 'scale-100 opacity-100'}
                `}>

                    {/* HUD Header */}
                    <div className="absolute top-4 w-full flex justify-between px-8 text-[11px] font-serif uppercase tracking-[0.1em] opacity-40 text-slate-300 max-w-6xl mx-auto left-0 right-0">
                        <span className="pointer-events-none">Le Cortex des Souvenirs</span>
                        <button
                            onClick={() => setShowHelp(true)}
                            className="pointer-events-auto hover:text-white hover:opacity-100 transition-all font-sans"
                        >[ Aide ]</button>
                        <span className={`pointer-events-none ${v3State.phase === 'VERDICT' ? 'text-amber-200/80 font-bold' : 'text-slate-300'}`}>
                            Étape : {getPhaseName(v3State.phase)}
                        </span>
                    </div>

                    {opponents.map((bot, idx) => {
                        const isActive = bot.id === activePlayerId;
                        const isCooldown = bot.id === v3State.lastTargetId && activePlayerId === localPlayerId;
                        const isHighlighted = tutorialHighlight?.targetPlayerId === bot.id;
                        const hue = stringToHue(bot.pseudo || bot.id);

                        return (
                            <motion.div
                                key={bot.id}
                                initial={{ y: -30, opacity: 0, scale: 0.92 }}
                                animate={{ y: 0, opacity: isCooldown ? 0.35 : 1, scale: isActive ? 1.03 : 1 }}
                                transition={{ delay: idx * 0.1, type: 'spring', stiffness: 200, damping: 20 }}
                                className={`
                                    w-64 lg:w-72 2xl:w-96 flex-none snap-center flex flex-col items-center gap-3
                                    ${isHighlighted ? 'ring-2 ring-yellow-400 shadow-[0_0_50px_rgba(250,204,21,0.5)] z-50 rounded-xl' : ''}
                                    transition-all relative group
                                `}
                            >
                                {/* Avatar */}
                                <div className={`
                                    relative p-[2px] rounded-full transition-all duration-300
                                    ${isActive ? 'shadow-[0_0_15px_rgba(251,191,36,0.2)]' : 'opacity-80'}
                                `}>
                                    <div
                                        className="w-16 h-16 rounded-full flex items-center justify-center text-lg font-bold text-white overflow-hidden relative avatar-bubble shadow-inner border border-white/10"
                                        style={{ '--avatar-hue': hue } as React.CSSProperties}
                                    >
                                        <div className="absolute inset-0 bg-white/5 opacity-20 pointer-events-none" />
                                        <span className="z-10 font-serif drop-shadow-md">
                                            {bot.pseudo?.substring(0, 2).toUpperCase() || '??'}
                                        </span>
                                    </div>
                                    {isActive && (
                                        <motion.div
                                            className="absolute -top-9 left-1/2 -translate-x-1/2 text-[10px] text-amber-200/70 font-serif whitespace-nowrap blur-[0.5px]"
                                            animate={{ opacity: [0.6, 1, 0.6] }}
                                            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                                        >
                                            * Observe *
                                        </motion.div>
                                    )}
                                    {/* Lucidity ring */}
                                    <svg className="absolute inset-[-4px] w-[calc(100%+8px)] h-[calc(100%+8px)] -rotate-90" viewBox="0 0 64 64">
                                        <circle cx="32" cy="32" r="30" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
                                        <circle
                                            cx="32" cy="32" r="30" fill="none"
                                            stroke={isActive ? 'rgba(251,191,36,0.5)' : `hsl(${hue},40%,60%)`}
                                            strokeWidth="1.5"
                                            strokeDasharray={`${188 * bot.lucidity / 100} 188`}
                                            strokeLinecap="round"
                                            className="transition-all duration-700"
                                        />
                                    </svg>
                                </div>

                                {/* Pseudo + Lucidity */}
                                <div className="flex flex-col items-center gap-0.5">
                                    <div className={`text-xs font-serif tracking-wide ${isActive ? 'text-amber-100' : 'text-slate-400'}`}>
                                        {bot.pseudo}
                                    </div>
                                    <div className={`text-[10px] font-serif italic ${isActive ? 'text-amber-200/50' : 'text-slate-500'}`}>
                                        Éveil: {bot.lucidity}%
                                    </div>
                                </div>

                                {/* Grille adverse */}
                                <div className={`
                                    w-64 h-64 lg:w-72 lg:h-72 2xl:w-96 2xl:h-96
                                    bg-slate-900/30 backdrop-blur-md rounded-lg p-2
                                    grid grid-cols-10 grid-rows-10 gap-[2px]
                                    shadow-2xl relative overflow-hidden
                                    transition-all duration-500 border
                                    ${isActive
                                        ? 'border-white/20 shadow-[0_0_30px_rgba(255,255,255,0.05)]'
                                        : 'border-white/5 hover:border-white/10'
                                    }
                                `}>
                                    {/* Grid glow overlay */}
                                    <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:10%_10%] z-0" />

                                    {bot.cortex.cells.flat().map((cell, i) => {
                                        const isTargetable = activePlayerId === localPlayerId && !isCooldown && !revealAll;
                                        return (
                                            <OpponentCell
                                                key={i}
                                                cell={cell}
                                                index={i}
                                                isTargetable={isTargetable}
                                                onProbe={() => onProbe(bot.id, i)}
                                                revealAll={revealAll}
                                            />
                                        );
                                    })}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {/* ════════════════════════════════════════
                    LOG SYSTÈME (Narratif)
                    ════════════════════════════════════════ */}
                <div className="h-36 mx-4 lg:mx-auto lg:w-3/4 2xl:w-2/3 mb-3 rounded-lg bg-slate-900/40 border border-white/5 flex flex-col font-serif text-[13px] overflow-hidden relative shadow-xl z-10 backdrop-blur-xl">
                    <div className="flex items-center gap-2 px-4 py-2 border-b border-white/5 bg-slate-800/20">
                        <span className="text-slate-400/50 text-[10px] uppercase tracking-widest italic">Échos Récents</span>
                    </div>

                    <div className="p-3 overflow-y-auto scrollbar-hide flex flex-col-reverse h-full space-y-reverse space-y-1 pointer-events-none select-none">
                        <AnimatePresence initial={false}>
                            {v3State.log.slice().reverse().slice(0, 10).map((entry, i) => (
                                <motion.div
                                    key={`${entry}-${i}`}
                                    initial={{ x: -20, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.25 }}
                                    className={`leading-tight flex items-center gap-2 ${getLogClass(entry)}`}
                                >
                                    <span className="opacity-40 text-[9px] text-slate-500 shrink-0">
                                        {new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                    </span>
                                    <span className="text-[10px] shrink-0">{getLogIcon(entry)}</span>
                                    <span>{entry}</span>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>
                </div>

                {/* ════════════════════════════════════════
                    COCKPIT JOUEUR (bas)
                    ════════════════════════════════════════ */}
                {localPlayer && (
                    <motion.div
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                        className={`
                            w-full h-auto lg:flex-1
                            cockpit-panel p-4 lg:p-6
                            flex justify-center items-center relative z-30
                            transition-all duration-500
                            ${!isMyTurn ? 'grayscale-[0.7] opacity-80' : ''}
                        `}
                    >
                        {/* Indicateurs gauche */}
                        <div className="absolute left-6 lg:left-10 flex flex-col gap-3 opacity-60 hidden lg:flex">
                            {[1, 2, 3].map(i => (
                                <div key={i} className="flex items-center gap-2">
                                    <motion.div
                                        animate={{ scale: isMyTurn ? [1, 1.3, 1] : 1, opacity: isMyTurn ? 1 : 0.3 }}
                                        transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.2 }}
                                        className={`w-2 h-2 rounded-full ${isMyTurn ? 'bg-amber-500' : 'bg-slate-700'}`}
                                    />
                                    <div className="text-[9px] font-mono text-slate-600 uppercase">ECHO_{i.toString().padStart(2, '0')}</div>
                                </div>
                            ))}
                        </div>

                        {/* Console principale */}
                        <div className="flex flex-col lg:flex-row gap-6 lg:gap-16 items-center w-full lg:w-auto justify-center">

                            {/* Stats joueur */}
                            <div className="flex flex-row lg:flex-col gap-4 lg:gap-3 items-center lg:items-end justify-center">
                                <div className="flex flex-col items-end gap-1">
                                    <div className="text-[11px] font-serif text-slate-400 capitalize italic">Votre Éveil</div>
                                    <motion.div
                                        key={localPlayer.lucidity}
                                        initial={{ scale: 1.1, color: '#f8fafc' }}
                                        animate={{ scale: 1, color: localPlayer.lucidity < 30 ? '#ef4444' : '#f8fafc' }}
                                        className="text-4xl 2xl:text-6xl font-serif"
                                    >
                                        {localPlayer.lucidity}%
                                    </motion.div>
                                    <div className="w-28 h-1 bg-white/5 rounded-full overflow-hidden">
                                        <motion.div
                                            animate={{ width: `${localPlayer.lucidity}%` }}
                                            transition={{ type: 'spring' }}
                                            className={`h-full rounded-full transition-colors duration-700 ${localPlayer.lucidity < 30
                                                ? 'bg-red-400/50'
                                                : 'bg-white/30'
                                                }`}
                                        />
                                    </div>
                                    <div className="text-[10px] font-serif uppercase tracking-widest mt-1 text-slate-300">
                                        {localPlayer.pseudo || 'Explorateur'}
                                    </div>
                                </div>
                            </div>

                            {/* Mon Plateau */}
                            <div className={`
                                w-full max-w-[320px] lg:max-w-[380px] 2xl:max-w-[500px] aspect-square
                                bg-black rounded-lg border-2 p-2 lg:p-3
                                grid grid-cols-10 grid-rows-10 gap-[2px]
                                shadow-2xl relative overflow-hidden
                                transition-all duration-500
                                ${isMyTurn
                                    ? 'border-amber-500/60 shadow-[0_0_40px_rgba(251,191,36,0.2)]'
                                    : 'border-slate-700/40'}
                            `}>
                                {/* Holographic overlay */}
                                <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(rgba(34,211,238,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(34,211,238,0.025)_1px,transparent_1px)] bg-[size:10%_10%] z-0" />
                                {/* Rotating gradient hint */}
                                <motion.div
                                    className="absolute inset-0 pointer-events-none z-0 opacity-5"
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
                                    style={{ background: 'conic-gradient(from 0deg, rgba(168,85,247,0.5), transparent, rgba(34,211,238,0.5), transparent)' }}
                                />

                                {localPlayer.cortex.cells.flat().map((cell, i) => (
                                    <MyCell key={i} cell={cell} />
                                ))}
                            </div>

                            {/* Indicateur de TOUR */}
                            <div className="flex flex-col items-center gap-3 lg:pb-8 hidden lg:flex">
                                <div className={`
                                    w-24 h-24 2xl:w-28 2xl:h-28 rounded-full flex items-center justify-center
                                    transition-all duration-1000 relative border
                                    ${isMyTurn
                                        ? 'bg-white/5 border-white/20 shadow-[0_0_40px_rgba(255,255,255,0.05)]'
                                        : 'bg-transparent border-white/5'
                                    }
                                `}>
                                    {/* Anneaux tournants (mon tour) */}
                                    {isMyTurn && (
                                        <>
                                            <div className="absolute inset-[-1px] rounded-full border-t border-r border-white/40 turn-go-ring" />
                                            <div className="absolute inset-[-6px] rounded-full border-t border-white/10 turn-go-ring" style={{ animationDuration: '4s', animationDirection: 'reverse' }} />
                                        </>
                                    )}
                                    {/* Radar (attente) */}
                                    {!isMyTurn && (
                                        <>
                                            <div className="radar-ring" />
                                            <div className="radar-ring" style={{ animationDelay: '2s' }} />
                                        </>
                                    )}
                                    <span className={`
                                        text-xl 2xl:text-2xl font-serif italic tracking-widest
                                        ${isMyTurn ? 'text-white turn-go-pulse-text' : 'text-slate-600'}
                                    `}>
                                        {isMyTurn ? 'Agir' : '...'}
                                    </span>
                                </div>
                                <div className="text-[11px] font-serif uppercase tracking-[0.1em] text-center">
                                    {isMyTurn
                                        ? <motion.span
                                            className="text-white/80"
                                            animate={{ opacity: [1, 0.6, 1] }}
                                            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                                        >À votre tour</motion.span>
                                        : <span className="text-slate-600">Patience...</span>
                                    }
                                </div>
                            </div>

                        </div>
                    </motion.div>
                )}
            </div>

            {/* ── MODALS ── */}
            <AnimatePresence>
                {v3State.phase === 'VERDICT' && (
                    <VerdictModal
                        revealedSouvenir={v3State.currentSouvenirRevealed || 'Souvenir Inconnu'}
                        question={v3State.currentVerdictQuestion || 'Pourquoi ce souvenir est-il important ?'}
                        options={v3State.currentVerdictQuestionOptions}
                        isActivePlayer={activePlayerId === localPlayerId}
                        onConfirm={(isCorrect) => onVerdict(isCorrect)}
                    />
                )}
            </AnimatePresence>

            <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} />
        </div>
    );
};

export default GameArenaUI;
